/**
 * DRAG_WORDS: drag word cards from a bank into the slots of a text.
 *
 * The author brackets each answer right in the Text or the Question:
 * `The [[cat]] sat on the [[mat]].` Every `[[...]]` becomes an empty slot and
 * its word a card in the bank. `extraWords` adds distractor cards, so the bank
 * either matches the slots one to one or holds more cards than slots.
 *
 * A card can sit in one slot at a time. A slot is correct when the card in it
 * reads the same as the slot's answer, so two identical cards are
 * interchangeable — the only way one text can have two correct placements.
 *
 * Shared with the admin editor (sensay-assessment-admin:
 * src/utils/dragWords.js). Keep the two copies in sync.
 */

import {
  BLANK_FIELDS,
  escapeHtml,
  normalizeBlankText,
  splitOnBlankMarkers,
} from "./bracketBlanks.mjs";

// FNV-1a: turns the seed string into a 32-bit PRNG state.
function hashSeed(seed) {
  let hash = 2166136261;
  const source = String(seed);
  for (let i = 0; i < source.length; i++) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

// mulberry32: small, fast, and the same sequence on every browser.
function seededRandom(seed) {
  let state = hashSeed(seed);
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The bank order for `texts`, stable for a given seed so a reload shows the
 * same bank. The first `answerCount` texts are the slot answers in slot
 * order, i.e. the answer key: whenever the words differ, the bank never
 * starts with that sequence, whatever the extra words after it do.
 */
export function shuffleWords(texts = [], seed = "", answerCount = texts.length) {
  const original = (texts || []).slice();
  if (new Set(original).size < 2) return original;
  const keyLength = Math.max(0, Math.min(answerCount, original.length));
  const hidesKey = (order) =>
    keyLength === 0 || order.slice(0, keyLength).some((text, i) => text !== original[i]);

  for (let attempt = 0; attempt < 5; attempt++) {
    const random = seededRandom(`${seed}:${attempt}`);
    const result = original.slice();
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    if (hidesKey(result)) return result;
  }
  // Swapping the first card with one that reads differently always changes
  // the first card, so the bank no longer opens with the key.
  const fallback = original.slice();
  const other = fallback.findIndex((text) => text !== fallback[0]);
  [fallback[0], fallback[other]] = [fallback[other], fallback[0]];
  return fallback;
}

/**
 * Reads a DRAG_WORDS definition.
 *
 * @param data     the exercise data (`question`, `text`, `extraWords`).
 * @param sanitize the platform's HTML normalization, applied before scanning.
 * @param seed     bank shuffle seed; the platform passes instance + exercise
 *                 so each candidate gets their own order.
 *
 * Returns `{ fields, slots, words, extraWordCount }`: `fields.question` /
 * `fields.text` are the parts of each field (see splitOnBlankMarkers),
 * `slots[i]` is `{ index, field, answer }` in document order, and `words[i]`
 * is `{ index, text }` in bank order.
 */
export function parseDragWords(data, { sanitize = (value) => value, seed = "" } = {}) {
  const slots = [];
  const fields = {};

  BLANK_FIELDS.forEach((name) => {
    const value = data?.[name];
    if (value === null || value === undefined || value === "") {
      fields[name] = [];
      return;
    }
    fields[name] = splitOnBlankMarkers(sanitize(String(value)) ?? "", (content) => {
      const answer = normalizeBlankText(content);
      if (!answer) return null;
      const slot = { index: slots.length, field: name, answer };
      slots.push(slot);
      return slot;
    });
  });

  const extras = (Array.isArray(data?.extraWords) ? data.extraWords : [])
    .map(normalizeBlankText)
    .filter((word) => word.length > 0);
  const words = shuffleWords(
    [...slots.map((slot) => slot.answer), ...extras],
    seed,
    slots.length,
  ).map(
    (text, index) => ({ index, text }),
  );

  return { fields, slots, words, extraWordCount: extras.length };
}

/** No card in any slot. */
export function emptyPlacements(slots = []) {
  return (slots || []).map(() => null);
}

/**
 * Puts card `wordIndex` into `slotIndex`. A card already in another slot
 * moves, and swaps with whatever was in the target; a card coming from the
 * bank sends the target's previous card back to the bank.
 */
export function placeWord(placements = [], wordIndex, slotIndex) {
  const next = placements.slice();
  if (slotIndex < 0 || slotIndex >= next.length) return next;
  const from = next.indexOf(wordIndex);
  const displaced = next[slotIndex] ?? null;
  next[slotIndex] = wordIndex;
  if (from !== -1 && from !== slotIndex) next[from] = displaced;
  return next;
}

/** Sends the card in `slotIndex` back to the bank. */
export function removeWord(placements = [], slotIndex) {
  const next = placements.slice();
  if (slotIndex >= 0 && slotIndex < next.length) next[slotIndex] = null;
  return next;
}

/** Bank cards not sitting in a slot, in bank order. */
export function unplacedWordIndexes(words = [], placements = []) {
  const placed = new Set((placements || []).filter((w) => w !== null && w !== undefined));
  return (words || []).filter((word) => !placed.has(word.index)).map((word) => word.index);
}

/** How many slots are still empty. */
export function countEmptySlots(placements = []) {
  return (placements || []).filter((w) => w === null || w === undefined).length;
}

/**
 * The result payload for a DRAG_WORDS answer, evaluated client-side like
 * FILL_THE_GAP: the scoring service reads `selectedAnswerCorrectCount` /
 * `questionCount`. `dragAnswerList[i]` is `{ answer, correct }` for slot i,
 * `answer` being the text of the card placed there ("" when empty).
 */
export function scoreDragWords(slots = [], words = [], placements = []) {
  let correctCount = 0;
  const dragAnswerList = (slots || []).map((slot) => {
    const wordIndex = placements?.[slot.index];
    const word = wordIndex === null || wordIndex === undefined ? null : words?.[wordIndex];
    const answer = word ? normalizeBlankText(word.text) : "";
    const correct = answer !== "" && answer === normalizeBlankText(slot.answer);
    if (correct) correctCount++;
    return { answer, correct };
  });
  return {
    dragAnswerList,
    selectedAnswerCorrectCount: correctCount,
    questionCount: dragAnswerList.length,
  };
}

/**
 * Markup of one slot for the candidate. Injected via {@html}, so the
 * component finds slots through `data-dw-slot` and the card in a filled slot
 * through `data-dw-word`. `label(slotNumber, wordText)` names it for screen
 * readers (`wordText` is null while empty).
 */
export function renderDragSlot(slot, words = [], placements = [], label = () => "") {
  const wordIndex = placements?.[slot.index];
  const word = wordIndex === null || wordIndex === undefined ? null : words?.[wordIndex];
  const name = escapeHtml(label(slot.index + 1, word ? word.text : null));
  if (!word) {
    return (
      `<button type="button" class="dw-slot" data-dw-slot="${slot.index}" ` +
      `aria-label="${name}"><span class="dw-slot-empty"></span></button>`
    );
  }
  return (
    `<button type="button" class="dw-slot dw-slot-filled" data-dw-slot="${slot.index}" ` +
    `data-dw-word="${word.index}" aria-label="${name}">${escapeHtml(word.text)}</button>`
  );
}
